import type { Discount } from '@carvana/merch-contracts';
import {
  type ModalQueueGeneratedOpener,
  useModalQueue
} from '@carvana/merch-react-hooks';
import {
  MoreDetailsModalSections,
  MoreDetailsOptions
} from '@carvana/merch-tile';
import deepmerge from 'deepmerge';
import React, { createContext, useCallback, useMemo, useState } from 'react';
import { useIsMobile } from '~/context/common/DeviceContext';
import type { AppliedFilters } from '~/contracts/common';
import { SeoReviewData } from '~/contracts/common/Seo';
import type { DeepPartial } from '~/types';
import { ModalId, ModalPriorities } from './constants';

type WIPModalOptions = {
  vehicleId: number;
};

type ContextType = {
  filtersToSave: AppliedFilters;
  geicoDiscount: Discount | null;
  isAccountModalOpen: boolean;
  isActivationModalOpen: boolean;
  isAddCoApplicantModalOpen: boolean;
  isAnyModalOpen: boolean;
  isAppraisalRefreshModalOpen: boolean;
  isComposerUiModalOpen: boolean;
  isFilterModalOpen: boolean;
  isGeicoDiscountModalOpen: boolean;
  isGeolocationModalOpen: boolean;
  isGreatDealModalOpen: boolean;
  isMoreDetailsModalOpen: boolean;
  isRootDiscountModalOpen: boolean;
  isSeoReviewModalOpen: boolean;
  isSampleTermsModalOpen: boolean;
  isSaveSearchModalOpen: boolean;
  isSaveSearchNudgeOpen: boolean;
  isSearchBarFocused: boolean;
  isSearchBarModalOpen: boolean;
  isShippingModalOpen: boolean;
  isSoftPullModalOpen: boolean;
  isSortByModalOpen: boolean;
  isTradeIncentiveModalOpen: boolean;
  isWipNotificationModalOpen: boolean;
  moreDetailsOptions: MoreDetailsOptions;
  rootDiscount: Discount | null;
  seoReview: SeoReviewData | null;
  setAccountModalOpen: ModalQueueGeneratedOpener;
  setActivationModalOpen: ModalQueueGeneratedOpener;
  setAddCoApplicantModalOpen: ModalQueueGeneratedOpener;
  setAppraisalRefreshModalOpen: ModalQueueGeneratedOpener;
  setComposerUiModalOpen: ModalQueueGeneratedOpener;
  setFilterModalOpen: ModalQueueGeneratedOpener;
  setGeicoDiscountModalOpen: (isOpen: boolean, discount?: Discount) => void;
  setGeolocationModalOpen: ModalQueueGeneratedOpener;
  setGreatDealModalOpen: ModalQueueGeneratedOpener;
  setIsSearchBarFocused: (isFocused: boolean) => void;
  setMoreDetailsModalOpen: (
    isOpen: boolean,
    options?: MoreDetailsOptions
  ) => void;
  setRootDiscountModalOpen: (isOpen: boolean, discount?: Discount) => void;
  setSaveSearchModalOpen: (isOpen: boolean, filters: AppliedFilters) => void;
  setSampleTermsModalOpen: ModalQueueGeneratedOpener;
  setSaveSearchNudgeOpen: ModalQueueGeneratedOpener;
  setSearchBarModalOpen: ModalQueueGeneratedOpener;
  setSeoReviewModalOpen: (
    isOpen: boolean,
    options?: SeoReviewData | null
  ) => void;
  setShippingModalOpen: ModalQueueGeneratedOpener;
  setSoftPullModalOpen: ModalQueueGeneratedOpener;
  setSortByModalOpen: ModalQueueGeneratedOpener;
  setIsTradeIncentiveModalOpen: ModalQueueGeneratedOpener;
  setWipNotificationModalOpen: (
    isOpen: boolean,
    options?: WIPModalOptions
  ) => void;
  wipModalVehicleId: number;
};

export const defaultContext: ContextType = {
  filtersToSave: {},
  geicoDiscount: null,
  isAccountModalOpen: false,
  isActivationModalOpen: false,
  isAddCoApplicantModalOpen: false,
  isAnyModalOpen: false,
  isAppraisalRefreshModalOpen: false,
  isComposerUiModalOpen: false,
  isFilterModalOpen: false,
  isGeicoDiscountModalOpen: false,
  isGeolocationModalOpen: false,
  isGreatDealModalOpen: false,
  isMoreDetailsModalOpen: false,
  isRootDiscountModalOpen: false,
  isSeoReviewModalOpen: false,
  isSampleTermsModalOpen: false,
  isSaveSearchModalOpen: false,
  isSaveSearchNudgeOpen: false,
  isSearchBarFocused: false,
  isSearchBarModalOpen: false,
  isShippingModalOpen: false,
  isSoftPullModalOpen: false,
  isSortByModalOpen: false,
  isTradeIncentiveModalOpen: false,
  isWipNotificationModalOpen: false,
  moreDetailsOptions: {
    openTo: MoreDetailsModalSections.PriceDetails
  },
  rootDiscount: null,
  seoReview: null,
  setAccountModalOpen() {},
  setActivationModalOpen() {},
  setAddCoApplicantModalOpen() {},
  setAppraisalRefreshModalOpen() {},
  setComposerUiModalOpen() {},
  setFilterModalOpen() {},
  setGeicoDiscountModalOpen() {},
  setGeolocationModalOpen() {},
  setGreatDealModalOpen() {},
  setIsSearchBarFocused() {},
  setMoreDetailsModalOpen() {},
  setRootDiscountModalOpen() {},
  setSeoReviewModalOpen() {},
  setSampleTermsModalOpen() {},
  setSaveSearchModalOpen() {},
  setSaveSearchNudgeOpen() {},
  setSearchBarModalOpen() {},
  setShippingModalOpen() {},
  setSoftPullModalOpen() {},
  setSortByModalOpen() {},
  setIsTradeIncentiveModalOpen() {},
  setWipNotificationModalOpen() {},
  wipModalVehicleId: 0
};

const ModalContext = createContext<ContextType>(defaultContext);

export default ModalContext;

export type OwnProps = React.PropsWithChildren;

export function ModalContextProvider({ children }: OwnProps) {
  const isMobile = useIsMobile();

  const [currentModalId, generateOpener, modalQueue] =
    useModalQueue(ModalPriorities);
  const [filtersToSave, setFiltersToSave] = useState<AppliedFilters>({});
  const [geicoDiscount, setGeicoDiscount] = useState<Discount | null>(
    defaultContext.geicoDiscount
  );
  const [rootDiscount, setRootDiscount] = useState<Discount | null>(null);
  const [moreDetailsOptions, setMoreDetailsOptions] =
    useState<MoreDetailsOptions>(defaultContext.moreDetailsOptions);
  const [seoReview, setSeoReview] = useState<SeoReviewData | null>(
    defaultContext.seoReview
  );
  const [isSearchBarFocused, setIsSearchBarFocused] = useState(false);
  const [wipModalVehicleId, setWipModalVehicleId] = useState(
    defaultContext.wipModalVehicleId
  );

  const {
    setAccountModalOpen,
    setActivationModalOpen,
    setAppraisalRefreshModalOpen,
    setAddCoApplicantModalOpen,
    setComposerUiModalOpen,
    setFilterModalOpen,
    setGeicoDiscountModalOpen,
    setGeolocationModalOpen,
    setGreatDealModalOpen,
    setMoreDetailsModalOpen,
    setRootDiscountModalOpen,
    setSeoReviewModalOpen,
    setSampleTermsModalOpen,
    setSaveSearchModalOpen,
    setSaveSearchNudgeOpen,
    setSearchBarModalOpen,
    setShippingModalOpen,
    setSoftPullModalOpen,
    setSortByModalOpen,
    setIsTradeIncentiveModalOpen,
    setWipNotificationModalOpen
  } = useMemo(
    () => ({
      setAccountModalOpen: generateOpener(ModalId.Account),
      setActivationModalOpen: generateOpener(ModalId.Activation),
      setAppraisalRefreshModalOpen: generateOpener(ModalId.AppraisalRefresh),
      setAddCoApplicantModalOpen: generateOpener(ModalId.AddCoApplicant),
      setComposerUiModalOpen: generateOpener(ModalId.ComposerUi),
      setFilterModalOpen: generateOpener(ModalId.FilterMenu),
      setGeicoDiscountModalOpen: generateOpener(ModalId.GeicoDiscount),
      setGeolocationModalOpen: generateOpener(ModalId.Geolocation),
      setGreatDealModalOpen: generateOpener(ModalId.GreatDeal),
      setMoreDetailsModalOpen: generateOpener(ModalId.MoreDetails),
      setRootDiscountModalOpen: generateOpener(ModalId.RootDiscount),
      setSeoReviewModalOpen: generateOpener(ModalId.Review),
      setSampleTermsModalOpen: generateOpener(ModalId.SampleTermsModal),
      setSaveSearchModalOpen: generateOpener(ModalId.SaveSearchModal),
      setSaveSearchNudgeOpen: generateOpener(ModalId.SaveSearchNudge),
      setSearchBarModalOpen: generateOpener(ModalId.SearchBar),
      setShippingModalOpen: generateOpener(ModalId.Shipping),
      setSoftPullModalOpen: generateOpener(ModalId.SoftPull),
      setSortByModalOpen: generateOpener(ModalId.SortBy),
      setIsTradeIncentiveModalOpen: generateOpener(ModalId.TradeIncentive),
      setWipNotificationModalOpen: generateOpener(ModalId.WIPNotifications)
    }),
    [generateOpener]
  );

  const setSavedSearchModalOpenWithFiltersToSave = useCallback(
    (isOpen: boolean, filters: Partial<AppliedFilters>) => {
      setFiltersToSave(filters);
      setSaveSearchModalOpen(isOpen);
    },
    [setSaveSearchModalOpen]
  );

  const setMoreDetailsModalOpenWithVehicle = useCallback<
    ContextType['setMoreDetailsModalOpen']
  >(
    (isOpen: boolean, options?: MoreDetailsOptions) => {
      if (options) setMoreDetailsOptions(options);
      setMoreDetailsModalOpen(isOpen);
    },
    [setMoreDetailsModalOpen]
  );

  const setSeoReviewModalOpenWithReview = useCallback<
    ContextType['setSeoReviewModalOpen']
  >(
    (isOpen: boolean, options?: SeoReviewData | null) => {
      if (options) setSeoReview(options);
      if (!isOpen) setSeoReview(null);
      setSeoReviewModalOpen(isOpen);
    },
    [setSeoReviewModalOpen]
  );

  const setGeicoDiscountModalOpenWithDiscount = useCallback<
    ContextType['setGeicoDiscountModalOpen']
  >(
    (isOpen: boolean, discount?: Discount) => {
      if (discount) setGeicoDiscount(discount);
      if (!isOpen) setGeicoDiscount(null);
      setGeicoDiscountModalOpen(isOpen);
    },
    [setGeicoDiscountModalOpen]
  );

  const setRootDiscountModalOpenWithDiscount = useCallback<
    ContextType['setRootDiscountModalOpen']
  >(
    (isOpen: boolean, discount?: Discount) => {
      if (discount) setRootDiscount(discount);
      if (!isOpen) setRootDiscount(null);
      setRootDiscountModalOpen(isOpen);
    },
    [setRootDiscountModalOpen]
  );

  const setWipNotificationModalOpenWithId = useCallback<
    ContextType['setWipNotificationModalOpen']
  >(
    (isOpen: boolean, options?: WIPModalOptions) => {
      if (options) setWipModalVehicleId(options.vehicleId);
      setWipNotificationModalOpen(isOpen);
    },
    [setWipNotificationModalOpen]
  );

  const value = useMemo<ContextType>(
    (): ContextType => ({
      filtersToSave: filtersToSave,
      geicoDiscount,
      isAccountModalOpen: currentModalId === ModalId.Account,
      isActivationModalOpen: currentModalId === ModalId.Activation,
      isAddCoApplicantModalOpen: currentModalId === ModalId.AddCoApplicant,
      isAnyModalOpen: currentModalId > -1,
      isAppraisalRefreshModalOpen: currentModalId === ModalId.AppraisalRefresh,
      isComposerUiModalOpen: currentModalId === ModalId.ComposerUi,
      isFilterModalOpen: modalQueue.has(ModalId.FilterMenu),
      isGeicoDiscountModalOpen: currentModalId === ModalId.GeicoDiscount,
      isGeolocationModalOpen: currentModalId === ModalId.Geolocation,
      isGreatDealModalOpen: currentModalId === ModalId.GreatDeal,
      isMoreDetailsModalOpen: isMobile
        ? modalQueue.has(ModalId.MoreDetails)
        : currentModalId === ModalId.MoreDetails,
      isRootDiscountModalOpen: currentModalId === ModalId.RootDiscount,
      isSeoReviewModalOpen: currentModalId === ModalId.Review,
      isSampleTermsModalOpen: currentModalId === ModalId.SampleTermsModal,
      isSaveSearchModalOpen: currentModalId === ModalId.SaveSearchModal,
      isSaveSearchNudgeOpen: currentModalId === ModalId.SaveSearchNudge,
      isSearchBarFocused,
      isSearchBarModalOpen: currentModalId === ModalId.SearchBar,
      isShippingModalOpen: currentModalId === ModalId.Shipping,
      isSoftPullModalOpen: currentModalId === ModalId.SoftPull,
      isSortByModalOpen: currentModalId === ModalId.SortBy,
      isTradeIncentiveModalOpen: currentModalId === ModalId.TradeIncentive,
      isWipNotificationModalOpen: currentModalId === ModalId.WIPNotifications,
      moreDetailsOptions,
      rootDiscount,
      seoReview,
      setAccountModalOpen,
      setActivationModalOpen,
      setAddCoApplicantModalOpen,
      setAppraisalRefreshModalOpen,
      setComposerUiModalOpen,
      setFilterModalOpen,
      setGeicoDiscountModalOpen: setGeicoDiscountModalOpenWithDiscount,
      setGeolocationModalOpen,
      setGreatDealModalOpen,
      setIsSearchBarFocused,
      setMoreDetailsModalOpen: setMoreDetailsModalOpenWithVehicle,
      setRootDiscountModalOpen: setRootDiscountModalOpenWithDiscount,
      setSeoReviewModalOpen: setSeoReviewModalOpenWithReview,
      setSampleTermsModalOpen,
      setSaveSearchModalOpen: setSavedSearchModalOpenWithFiltersToSave,
      setSaveSearchNudgeOpen,
      setSearchBarModalOpen,
      setShippingModalOpen,
      setSoftPullModalOpen,
      setSortByModalOpen,
      setIsTradeIncentiveModalOpen,
      setWipNotificationModalOpen: setWipNotificationModalOpenWithId,
      wipModalVehicleId
    }),
    [
      currentModalId,
      filtersToSave,
      geicoDiscount,
      isMobile,
      isSearchBarFocused,
      modalQueue,
      moreDetailsOptions,
      rootDiscount,
      seoReview,
      setAccountModalOpen,
      setActivationModalOpen,
      setAddCoApplicantModalOpen,
      setAppraisalRefreshModalOpen,
      setComposerUiModalOpen,
      setFilterModalOpen,
      setGeicoDiscountModalOpenWithDiscount,
      setGeolocationModalOpen,
      setGreatDealModalOpen,
      setIsTradeIncentiveModalOpen,
      setMoreDetailsModalOpenWithVehicle,
      setRootDiscountModalOpenWithDiscount,
      setSampleTermsModalOpen,
      setSaveSearchNudgeOpen,
      setSavedSearchModalOpenWithFiltersToSave,
      setSearchBarModalOpen,
      setSeoReviewModalOpenWithReview,
      setShippingModalOpen,
      setSoftPullModalOpen,
      setSortByModalOpen,
      setWipNotificationModalOpenWithId,
      wipModalVehicleId
    ]
  );

  return (
    <ModalContext.Provider value={value}>{children}</ModalContext.Provider>
  );
}

ModalContextProvider.displayName = 'ModalContextProvider';

export const generateModalContext = (
  customState: DeepPartial<ContextType>
): ContextType =>
  deepmerge<ContextType, DeepPartial<ContextType>>(defaultContext, customState);

export function MockedModalContextProvider({
  children,
  mockValue
}: React.PropsWithChildren<{ mockValue: DeepPartial<ContextType> }>) {
  return (
    <ModalContext.Provider value={generateModalContext(mockValue)}>
      {children}
    </ModalContext.Provider>
  );
}

export function useModalContext() {
  return React.useContext(ModalContext);
}
